import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsContent } from "@/components/ui/tabs";
import { ColorTab } from "@/components/ui/color-tab";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { inr, fmtDate, fmtDateTime } from "@/lib/utils-format";
import { Trash2, ClipboardList, ShieldCheck, Loader2, CheckCircle2, XCircle } from "lucide-react";

const HO_ROLES = ["super_admin", "admin", "ho_admin", "chief_accountant", "warehouse_manager", "accountant"];
const STATUS_CLS = { pending: "bg-amber-100 text-amber-800 border-amber-300", approved: "bg-emerald-100 text-emerald-800 border-emerald-300", rejected: "bg-red-100 text-red-700 border-red-300" };
const StatusBadge = ({ s, testid }) => <Badge variant="outline" className={`capitalize ${STATUS_CLS[s] || ""}`} data-testid={testid}>{s}</Badge>;

function RequestDialog({ batch, onClose, onDone }) {
  const [qty, setQty] = useState(batch.available_qty);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!(Number(qty) > 0)) return toast.error("Quantity must be greater than 0");
    if (Number(qty) > batch.available_qty + 0.0001) return toast.error(`Only ${batch.available_qty} available on this batch`);
    if (reason.trim().length < 5) return toast.error("Reason is required (min 5 characters)");
    setBusy(true);
    try {
      const { data } = await api.post("/disposals", { branch_id: batch.branch_id, product_id: batch.product_id, batch_id: batch.batch_id, quantity: Number(qty), reason: reason.trim() });
      toast.success(`Disposal request ${data.number} sent for Super Admin approval`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-white" data-testid="disposal-request-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit]">Raise Disposal Request</DialogTitle>
          <DialogDescription className="text-xs text-[#8B7355]">{batch.product_name} · batch {batch.batch_no} · expired {fmtDate(batch.expiry_date)} · {batch.branch_name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Quantity to dispose ({batch.unit}) · available {batch.available_qty}</Label>
            <Input type="number" min="0" step="any" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="disposal-request-qty" /></div>
          <div><Label>Reason *</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Expired on shelf, mould visible — unfit for sale" rows={3} data-testid="disposal-request-reason" /></div>
          <div className="text-xs text-[#8B7355]">Estimated write-off value: <b>{inr(Number(qty || 0) * (batch.rate || 0))}</b> (at HO transfer rate). Stock is deducted only once the Super Admin approves.</div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#C05621] hover:bg-[#9C4221] text-white" data-testid="disposal-request-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit for approval"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExpiredBatchesTab({ isHO, branches, onRequested }) {
  const [scope, setScope] = useState("");
  const [rows, setRows] = useState(null);
  const [target, setTarget] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/disposals/expired-batches", { params: scope ? { branch_id: scope } : {} }); setRows(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load expired batches"); setRows([]); }
  };
  useEffect(() => { load(); }, [scope]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-3">
      {isHO && (
        <select className="h-9 border border-[#EAE5D9] rounded-md px-2 text-sm bg-white" value={scope} onChange={(e) => setScope(e.target.value)} data-testid="disposal-scope">
          <option value="">All locations</option>
          {branches.map((b) => <option key={b.id} value={b.id}>{b.name} ({b.code})</option>)}
        </select>
      )}
      <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="expired-batches-table">
          <thead><tr className="text-left uc-label border-b border-[#EAE5D9]"><th className="px-4 py-3">Product</th><th className="px-4 py-3">Batch</th><th className="px-4 py-3">Location</th><th className="px-4 py-3">Expired on</th><th className="px-4 py-3 text-right">On hand</th><th className="px-4 py-3 text-right">Pending</th><th className="px-4 py-3 text-right">Value</th><th className="px-4 py-3"></th></tr></thead>
          <tbody>
            {rows === null && <tr><td colSpan={8} className="px-4 py-8 text-center text-[#8B7355]"><Loader2 className="w-4 h-4 animate-spin inline" /></td></tr>}
            {rows?.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-[#8B7355]" data-testid="expired-batches-empty">No expired batches with stock on hand. 🎉</td></tr>}
            {rows?.map((r) => (
              <tr key={`${r.branch_id}-${r.batch_id}`} className="border-t border-[#EAE5D9]" data-testid={`expired-batch-${r.branch_id}-${r.batch_id}`}>
                <td className="px-4 py-2"><div className="font-medium">{r.product_name}</div><div className="text-xs text-[#8B7355]">{r.sku}</div></td>
                <td className="px-4 py-2 font-mono text-xs">{r.batch_no}</td>
                <td className="px-4 py-2">{r.branch_name}</td>
                <td className="px-4 py-2 text-red-600">{fmtDate(r.expiry_date)}<div className="text-[11px]">{r.days_expired} day{r.days_expired === 1 ? "" : "s"} ago</div></td>
                <td className="px-4 py-2 text-right">{r.quantity} {r.unit}</td>
                <td className="px-4 py-2 text-right text-amber-700">{r.pending_qty || "—"}</td>
                <td className="px-4 py-2 text-right">{inr(r.value)}</td>
                <td className="px-4 py-2 text-right">
                  <Button size="sm" disabled={r.available_qty <= 0} onClick={() => setTarget(r)} className="bg-[#2C1810] hover:bg-[#1a0e09] text-white" data-testid={`disposal-raise-${r.branch_id}-${r.batch_id}`}><Trash2 className="w-3.5 h-3.5 mr-1" /> Dispose</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {target && <RequestDialog batch={target} onClose={() => setTarget(null)} onDone={() => { setTarget(null); load(); onRequested?.(); }} />}
    </div>
  );
}

function RequestsTable({ rows, actions, testid }) {
  return (
    <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
      <table className="w-full text-sm" data-testid={testid}>
        <thead><tr className="text-left uc-label border-b border-[#EAE5D9]"><th className="px-4 py-3">Request</th><th className="px-4 py-3">Product / Batch</th><th className="px-4 py-3">Location</th><th className="px-4 py-3 text-right">Qty</th><th className="px-4 py-3 text-right">Value</th><th className="px-4 py-3">Reason</th><th className="px-4 py-3">Status</th>{actions && <th className="px-4 py-3"></th>}</tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-[#8B7355]" data-testid={`${testid}-empty`}>Nothing here.</td></tr>}
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-[#EAE5D9] align-top" data-testid={`disposal-row-${r.id}`}>
              <td className="px-4 py-2"><div className="font-mono text-xs">{r.number}</div><div className="text-[11px] text-[#8B7355]">{r.requested_by_name} · {fmtDateTime(r.requested_at)}</div></td>
              <td className="px-4 py-2"><div className="font-medium">{r.product_name}</div><div className="text-xs text-[#8B7355] font-mono">{r.batch_no} · exp {fmtDate(r.expiry_date)}</div></td>
              <td className="px-4 py-2">{r.branch_name}</td>
              <td className="px-4 py-2 text-right">{r.quantity} {r.unit}</td>
              <td className="px-4 py-2 text-right">{inr(r.value)}</td>
              <td className="px-4 py-2 text-xs max-w-[220px]">{r.reason}{r.status === "rejected" && <div className="text-red-600 mt-1" data-testid={`disposal-rejection-${r.id}`}>Rejected: {r.rejection_reason}</div>}{r.status === "approved" && <div className="text-emerald-700 mt-1">Approved by {r.decided_by_name} · {fmtDateTime(r.decided_at)}{r.batch_disposed ? " · batch disposed" : ""}</div>}</td>
              <td className="px-4 py-2"><StatusBadge s={r.status} testid={`disposal-status-${r.id}`} /></td>
              {actions && <td className="px-4 py-2 text-right whitespace-nowrap">{actions(r)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RequestsTab({ reloadKey }) {
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/disposals", { params: status ? { status } : {} }).then((r) => setRows(r.data || [])).catch(() => setRows([])); }, [status, reloadKey]);
  return (
    <div className="space-y-3">
      <select className="h-9 border border-[#EAE5D9] rounded-md px-2 text-sm bg-white" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="disposal-status-filter">
        <option value="">All statuses</option><option value="pending">Pending</option><option value="approved">Approved</option><option value="rejected">Rejected</option>
      </select>
      <RequestsTable rows={rows} testid="disposal-requests-table" />
    </div>
  );
}

function ApprovalsTab({ onDecided }) {
  const [rows, setRows] = useState([]);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/disposals", { params: { status: "pending" } }).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const approve = async (r) => {
    setBusy(r.id);
    try { await api.post(`/disposals/${r.id}/approve`, {}); toast.success(`${r.number} approved — ${r.quantity} ${r.unit} written off at ${r.branch_name}`); load(); onDecided?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
    finally { setBusy(null); }
  };
  const reject = async () => {
    if (reason.trim().length < 3) return toast.error("Rejection reason is required");
    setBusy(rejecting.id);
    try { await api.post(`/disposals/${rejecting.id}/reject`, { reason: reason.trim() }); toast.success(`${rejecting.number} rejected`); setRejecting(null); setReason(""); load(); onDecided?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
    finally { setBusy(null); }
  };
  return (
    <div className="space-y-3">
      <div className="text-xs text-[#8B7355]">Approving deducts the quantity from that location, marks the batch disposed when exhausted, and posts Dr Waste &amp; Damage / Cr Stock. Rejecting returns the request to the requester with your reason.</div>
      <RequestsTable rows={rows} testid="disposal-approvals-table" actions={(r) => (
        <div className="flex gap-1 justify-end">
          <Button size="sm" onClick={() => approve(r)} disabled={busy === r.id} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`disposal-approve-${r.id}`}>{busy === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve</>}</Button>
          <Button size="sm" variant="outline" onClick={() => { setRejecting(r); setReason(""); }} disabled={busy === r.id} className="border-red-300 text-red-700 hover:bg-red-50" data-testid={`disposal-reject-${r.id}`}><XCircle className="w-3.5 h-3.5 mr-1" /> Reject</Button>
        </div>
      )} />
      <Dialog open={!!rejecting} onOpenChange={(o) => !o && setRejecting(null)}>
        <DialogContent className="max-w-md bg-white" data-testid="disposal-reject-dialog">
          <DialogHeader><DialogTitle className="font-[Outfit]">Reject {rejecting?.number}</DialogTitle><DialogDescription className="text-xs text-[#8B7355]">{rejecting?.product_name} × {rejecting?.quantity} at {rejecting?.branch_name}. The requester will see your reason.</DialogDescription></DialogHeader>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Why is this request being rejected?" data-testid="disposal-reject-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejecting(null)}>Cancel</Button>
            <Button onClick={reject} disabled={busy === rejecting?.id} className="bg-red-600 hover:bg-red-700 text-white" data-testid="disposal-reject-confirm">Reject request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function DisposalsPage() {
  const { user } = useAuth();
  const role = user?.role_id || user?.role;
  const isHO = HO_ROLES.includes(role);
  const isSuper = role === "super_admin";
  const [tab, setTab] = useState("expired");
  const [branches, setBranches] = useState([]);
  const [pending, setPending] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => { if (isHO) api.get("/branches").then((r) => setBranches(r.data || [])).catch(() => {}); }, [isHO]);
  const refreshPending = () => api.get("/disposals", { params: { status: "pending" } }).then((r) => setPending((r.data || []).length)).catch(() => {});
  useEffect(() => { refreshPending(); }, [reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const bump = () => setReloadKey((k) => k + 1);
  return (
    <div className="space-y-6" data-testid="disposals-page">
      <div>
        <div className="uc-label">{isHO ? "Head Office" : "Your Store"}</div>
        <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Expired Stock Disposal</h1>
        <p className="text-sm text-[#8B7355] mt-1">Raise disposal requests for expired batches; the Super Admin approves or rejects each one. Approved disposals appear in Stock Intelligence → Disposal Report.</p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-transparent flex-wrap h-auto p-0 gap-1">
          <ColorTab value="expired" tone="red" icon={Trash2} testid="disposals-tab-expired">Expired Batches</ColorTab>
          <ColorTab value="requests" tone="amber" icon={ClipboardList} testid="disposals-tab-requests">Requests{pending ? ` (${pending} pending)` : ""}</ColorTab>
          {isSuper && <ColorTab value="approvals" tone="green" icon={ShieldCheck} testid="disposals-tab-approvals">Approvals{pending ? ` (${pending})` : ""}</ColorTab>}
        </TabsList>
        <TabsContent value="expired" className="pt-4"><ExpiredBatchesTab isHO={isHO} branches={branches} onRequested={bump} /></TabsContent>
        <TabsContent value="requests" className="pt-4"><RequestsTab reloadKey={reloadKey} /></TabsContent>
        {isSuper && <TabsContent value="approvals" className="pt-4"><ApprovalsTab onDecided={bump} /></TabsContent>}
      </Tabs>
    </div>
  );
}
